/**
 * Admin Packages API Client
 * Handles all API calls for credit package management
 */

import axios from '@/lib/api/axios-instance';
import type {
  CreditPackage,
  PackageFormData,
  PackageApiResponse,
  PackageListResponse,
  PackageDetailResponse,
  PackageAnalytics,
} from '@/src/types/admin/package';

const BASE_URL = '/api/admin/credit-packages';

export const packageAPI = {
  /**
   * Get all credit packages
   * @param includeInactive - Include inactive packages in results
   */
  async getAll(includeInactive = false): Promise<PackageListResponse> {
    const response = await axios.get<PackageApiResponse<PackageListResponse>>(BASE_URL, {
      params: { include_inactive: includeInactive },
    });
    return response.data.data;
  },

  /**
   * Get single package by ID
   * @param id - Package UUID
   */
  async getById(id: string): Promise<CreditPackage> {
    const response = await axios.get<PackageApiResponse<PackageDetailResponse>>(
      `${BASE_URL}/${id}`
    );
    return response.data.data.package;
  },

  /**
   * Create new package
   * @param data - Package form data
   */
  async create(data: PackageFormData): Promise<CreditPackage> {
    const response = await axios.post<PackageApiResponse<{ package: CreditPackage }>>(
      BASE_URL,
      data
    );
    return response.data.data.package;
  },

  /**
   * Update existing package
   * @param id - Package UUID
   * @param data - Partial package form data
   */
  async update(id: string, data: Partial<PackageFormData>): Promise<CreditPackage> {
    const response = await axios.put<PackageApiResponse<{ package: CreditPackage }>>(
      `${BASE_URL}/${id}`,
      data
    );
    return response.data.data.package;
  },

  /**
   * Delete package
   * @param id - Package UUID
   * @param force - Force delete even if package has active customers
   */
  async delete(id: string, force = false): Promise<void> {
    await axios.delete(`${BASE_URL}/${id}`, {
      params: { force },
    });
  },

  /**
   * Toggle package active status
   * @param id - Package UUID
   * @param isActive - New active status
   */
  async toggleActive(id: string, isActive: boolean): Promise<CreditPackage> {
    const response = await axios.put<PackageApiResponse<{ package: CreditPackage }>>(
      `${BASE_URL}/${id}`,
      { is_active: isActive }
    );
    return response.data.data.package;
  },

  /**
   * Get package analytics
   */
  async getAnalytics(): Promise<PackageAnalytics> {
    const response = await axios.get<PackageApiResponse<PackageAnalytics>>(
      `${BASE_URL}/analytics`
    );
    return response.data.data;
  },
};
